/**
 * Pixel sprites from the asset catalogue (`src/game/assets`), drawn with CSS backgrounds at an
 * integer scale so they stay crisp (`image-rendering: pixelated`). Decorative by default.
 */
import type { CSSProperties } from 'react';
import { TILE_SIZE, type TileId } from '../game/assets/sheets';
import { spriteStyle } from './spriteStyle';
import { cx } from './cx';
import styles from './Sprite.module.css';

interface SpriteProps {
  tile: TileId;
  /** Integer scale; 2 draws a 16px tile at 32px. */
  scale?: number;
  className?: string;
  /** Accessible label; omit for decorative sprites (hidden from assistive tech). */
  label?: string;
  style?: CSSProperties;
}

export function Sprite({ tile, scale = 2, className, label, style }: SpriteProps) {
  const s = spriteStyle(tile, scale);
  const size = TILE_SIZE * scale;
  return (
    <span
      className={cx(styles.sprite, 'wb-pixel', className)}
      style={{ ...(s ?? { width: size, height: size }), ...style }}
      role={label ? 'img' : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
    />
  );
}

/** Several tiles stacked on top of each other (back to front), e.g. a character composite. */
export function SpriteStack({
  tiles,
  scale = 2,
  className,
  label,
}: {
  tiles: readonly TileId[];
  scale?: number;
  className?: string;
  label?: string;
}) {
  const size = TILE_SIZE * scale;
  return (
    <span
      className={cx(styles.stack, className)}
      style={{ width: size, height: size }}
      role={label ? 'img' : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
    >
      {tiles.map((t, i) => (
        <Sprite key={`${t}-${i}`} tile={t} scale={scale} className={styles.layer} />
      ))}
    </span>
  );
}
